import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCampaignProductsAction } from "../slices/campaignProductsSlice";
import { RootState } from "@/app/rootReducer";
import { FetchCampaignProductsRequest } from "@/modules/stochastic/features/CampaignManagement/api/fetchCampaignProducts/types";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

export function useCampaignProducts(refreshTrigger?: number) {
  const dispatch = useDispatch();
  const { campaignProducts, totalCount, loading, error } = useSelector(
    (state: RootState) => state.stochasticCampaignProducts,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  const [filters, setFilters] = useState<{
    searchTerm?: string;
  }>({
    searchTerm: "",
  });

  useEffect(() => {
    const requestData: FetchCampaignProductsRequest = {
      page: pagination.pageIndex + 1,
      sortBy: sorting.length > 0 && ["name", "customerPrice", "prospectPrice"].includes(sorting[0].id)
        ? sorting[0].id as "name" | "customerPrice" | "prospectPrice"
        : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : undefined,
      searchTerm: filters.searchTerm,
      pageSize: pagination.pageSize,
    };
    dispatch(fetchCampaignProductsAction(requestData));
  }, [dispatch, pagination, sorting, filters, refreshTrigger]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleFilterChange = (searchTerm: string) => {
    setFilters({ searchTerm });
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  return {
    campaignProducts,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
